/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Friday, November 12th 2021, 1:24:44 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import {
  TaskTemplate,
  NotificationTemplate,
  NotificationTemplateValidationSchema,
  TARGET_USERS,
  NOTIFICATION_TYPES,
} from "lib/schemas";
import { getNewNotificationTemplateDefault } from "lib/factories";
import { EmailIcon, SMSIcon } from "theme/icons";
import { TabulatedOne2ManyInlineInput } from "ui-widgets/forms";
import { APISERVICEPATHS } from "lib/api";
import { useOSFormContext } from "ui-widgets/forms/providers";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { NotificationEditButton } from "./NotificationEditButton";
import { EmptyNotificationsCTA } from "./EmptyNotificationsCTA";
import Stack from "@mui/material/Stack";

export const NotificationTemplateList = () => {
  const { notificationIsForHumanTask, watchedTaskTemplateId, getNewNotificationTemplate } =
    useNotificationTemplateListLogic();

  return (
    <>
      <TabulatedOne2ManyInlineInput<TaskTemplate, NotificationTemplate>
        apiServicePath={APISERVICEPATHS.notificationTemplates}
        foreignKey="task_template"
        foreignKeyId={watchedTaskTemplateId}
        uniqueIdentifier="id"
        getNewItemDefaultValues={getNewNotificationTemplate}
        relatedObjectValidationSchema={NotificationTemplateValidationSchema}
        renderNoDataMessage={(onClickNewItem) => <EmptyNotificationsCTA handleAddNewNotification={onClickNewItem} />}
        headerCells={[
          <Typography variant="subtitle2">Subject</Typography>,
          <Typography variant="subtitle2">User</Typography>,
          <Typography variant="subtitle2">Active</Typography>,
          <Typography variant="subtitle2">Type</Typography>,
          <>Edit</>,
        ]}
        renderBodyCells={({ rowItem: notificationTemplate, updateList }) => [
          <Typography>{notificationTemplate.title}</Typography>,
          <Typography>{TARGET_USERS[notificationTemplate.target_users]}</Typography>,
          notificationTemplate.enabled ? <CheckIcon color="success" /> : <CloseIcon color="error" />,
          <NotificationTypeDisplay type={notificationTemplate.type} />,
          <Stack direction="row" spacing={2}>
            <NotificationEditButton
              notificationTemplateFetchId={notificationTemplate.id}
              notificationTemplateDefaultValues={notificationTemplate}
              updateNotificationTemplateFromAPICallback={updateList}
              notificationIsForHumanTask={notificationIsForHumanTask}
            />
          </Stack>,
        ]}
      />
    </>
  );
};

const NotificationTypeDisplay = ({ type }: { type: NotificationTemplate["type"] }) => {
  const TypeIcon = type === "sms" ? SMSIcon : EmailIcon;

  return (
    <Tooltip title={<Typography variant="body1">{NOTIFICATION_TYPES[type]}</Typography>} placement="top" arrow>
      <TypeIcon color="secondary" width={30} height={30} />
    </Tooltip>
  );
};

const useNotificationTemplateListLogic = () => {
  const { watch } = useOSFormContext<TaskTemplate>();

  const [
    watchedTaskTemplateId,
    watchedTaskTemplateTitle,
    watchedTaskTemplateOrgId,
    watchedTaskTemplateType,
    watchedTaskTemplateIsClientTask,
  ] = watch<(keyof TaskTemplate)[]>(["id", "title", "artifact_org", "type", "client_task"]);

  const notificationIsForHumanTask = (watchedTaskTemplateType as TaskTemplate["type"]) === "View";

  const getNewNotificationTemplate = () =>
    getNewNotificationTemplateDefault({
      organizationId: watchedTaskTemplateOrgId as string,
      taskTemplateId: watchedTaskTemplateId as number,
      taskTemplateTitle: watchedTaskTemplateTitle as string,
      taskTemplateType: watchedTaskTemplateType as any,
      taskTemplateIsClientTask: watchedTaskTemplateIsClientTask as boolean,
    });

  return { notificationIsForHumanTask, watchedTaskTemplateId, getNewNotificationTemplate };
};
